import log from '@/lib/logger';
import {useEffect, useState} from "react";
import {useMembersTranslation, useCommonTranslation} from '@/i18n/hooks';
import {Dialog, DialogActions, DialogBody, DialogTitle} from "@/components/tailwind/dialog";
import {Button} from "@/components/tailwind/button";
import {ServiceGroup, ServiceWithGroupId} from "@/models/service";
import {Member, MemberService} from "@/models/member";
import {getListOfServiceGroups, getListOfServices} from "@/services/service-services";
import {useAuth} from "@/context/use-auth";
import {updateMemberServices} from "@/services/member-services";
import {Text} from "@/components/tailwind/text";
import ValidationAlert from "@/components/validation-alert";
import ServiceAssignmentPanel from "@/components/service-assignment-panel";

type MemberEditServicesDialogProps = {
    isOpen: boolean;
    onSave: () => void;
    onClose: () => void;
    memberData: Member;
    memberServices: MemberService[];
};

function MemberEditServicesDialog({
                                      isOpen,
                                      onSave,
                                      onClose,
                                      memberData,
                                      memberServices
                                  }: MemberEditServicesDialogProps) {
    const {t} = useMembersTranslation();
    const {t: tCommon} = useCommonTranslation();
    const {token} = useAuth();
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>([]);
    const [isDriver, setIsDriver] = useState(false);
    const [availableGroups, setAvailableGroups] = useState<ServiceGroup[]>([]);
    const [availableServices, setAvailableServices] = useState<ServiceWithGroupId[]>([]);

    useEffect(() => {
        if (isOpen && memberServices) {
            setSelectedServiceIds(
                memberServices.map(service => service.serviceId)
            );
            setIsDriver(memberData.isDriver ?? false);
        }
    }, [isOpen, memberServices, memberData.isDriver]);

    useEffect(() => {
        if (!isOpen) return;
        
        async function loadData() {
            setIsLoading(true);
            setErrorMessage(null);
            try {
                const [groupsResult, servicesResult] = await Promise.all([
                    getListOfServiceGroups(token),
                    getListOfServices(token, true)
                ]);

                if (groupsResult) {
                    setAvailableGroups(groupsResult);
                }

                if (servicesResult) {
                    setAvailableServices(servicesResult);
                }
            } catch (error) {
                log.error("Error fetching service groups and services:", error);
                setErrorMessage('memberEditDialog.services.errors.loading');
            } finally {
                setIsLoading(false);
            }
        }

        loadData();
    }, [isOpen, token]);

    async function handleSave() {
        if (selectedServiceIds.length === 0) {
            setErrorMessage(t('memberEditDialog.services.errors.active-member-requires-services'));
            return;
        }

        setIsLoading(true);
        setErrorMessage(null);

        updateMemberServices(token, {
            memberId: memberData.id,
            serviceIds: selectedServiceIds,
            isDriver: isDriver,
        }).then(() => {
            onSave()
            handleClose()
        }).catch((error) => {
            log.error("Error while updating member service associations:", error);
            if (error.status) {
                setErrorMessage(tCommon("serverError.response-code-with-details", {
                    statusCode: error.status,
                    errorDetails: error.error
                }))
            } else {
                setErrorMessage(tCommon("serverError.unknown-with-details", {
                    errorDetails: error.message || tCommon("serverError.unknown")
                }))
            }

        }).finally(() => setIsLoading(false))

    }

    function handleClose() {
        setErrorMessage(null);
        onClose();
    }

    return (
        <Dialog open={isOpen} onClose={handleClose}>
            <DialogTitle>
                {t('memberEditDialog.services.title')}
            </DialogTitle>

            <DialogBody>
                {errorMessage && (
                    <ValidationAlert message={errorMessage === 'memberEditDialog.services.errors.loading' ? t(errorMessage) : errorMessage}/>
                )}

                <Text className="mb-6">
                    {t('memberEditDialog.services.description')}
                </Text>

                <ServiceAssignmentPanel
                    services={availableServices}
                    groups={availableGroups}
                    selectedServiceIds={selectedServiceIds}
                    onServiceChange={setSelectedServiceIds}
                    isDriver={isDriver}
                    onDriverChange={setIsDriver}
                    isLoading={isLoading}
                />

                <DialogActions>
                    <Button
                        plain
                        onClick={handleClose}
                        disabled={isLoading}
                        aria-label={tCommon('buttons.cancel.ariaLabel')}
                    >
                        {tCommon('buttons.cancel.text')}
                    </Button>
                    <Button
                        onClick={handleSave}
                        disabled={isLoading}
                        aria-label={tCommon('buttons.submit.ariaLabel')}
                    >
                        {tCommon('buttons.submit.text')}
                    </Button>
                </DialogActions>
            </DialogBody>
        </Dialog>
    );
}

export default MemberEditServicesDialog;
